import {Injectable, inject} from '@angular/core';
import {LanguageService} from './language.service';
import {FeedbackLogicRuleAction} from '../types/generated/feedbackLogicRuleAction';
import {FeedbackLogicRuleOperator} from '../types/generated/feedbackLogicRuleOperator';
import {FeedbackQuestionType} from '../types/generated/feedbackQuestionType';
import {FeedbackStructureQuestionLogicRule} from '../types/feedback-types/feedbackStructureQuestionLogicRule.type';
import {FeedbackStructureQuestionFormQuestion} from '../types/feedback-types/feedbackStructureQuestionFormQuestion.type';
import {FeedbackStructureQuestionOption} from '../types/feedback-types/feedbackStructureQuestionOption.interface';

@Injectable({
	providedIn: 'root',
})
export class FeedbackLogicRuleTextBuilderService {
	private readonly languageService: LanguageService = inject(LanguageService);

	public buildRuleText(
		sourceQuestion: FeedbackStructureQuestionFormQuestion,
		logicRule: FeedbackStructureQuestionLogicRule,
		allQuestions: FeedbackStructureQuestionFormQuestion[],
		affectedQuestion?: FeedbackStructureQuestionFormQuestion
	): string {
		this.languageService.loadedLanguagesVersion$();

		const conditionText = this.buildConditionText(sourceQuestion, logicRule);
		const actionText = this.buildActionText(sourceQuestion, logicRule, allQuestions, affectedQuestion);

		return `${this.languageService.getTranslationByKey('FEEDBACKLOGICRULEIF')} ${conditionText}, ${actionText}.`;
	}

	public buildAffectedRuleTexts(
		question: FeedbackStructureQuestionFormQuestion,
		allQuestions: FeedbackStructureQuestionFormQuestion[]
	): string[] {
		this.languageService.loadedLanguagesVersion$();

		return this.sortedQuestions(allQuestions)
			.flatMap((sourceQuestion) => sourceQuestion.logic_rules.map((logicRule) => ({sourceQuestion, logicRule})))
			.filter(({sourceQuestion, logicRule}) => this.ruleAffectsQuestion(sourceQuestion, logicRule, question, allQuestions))
			.map(({sourceQuestion, logicRule}) => this.buildRuleText(sourceQuestion, logicRule, allQuestions, question));
	}

	public buildAffectedRuleTooltipText(
		question: FeedbackStructureQuestionFormQuestion,
		allQuestions: FeedbackStructureQuestionFormQuestion[]
	): string {
		return this.buildAffectedRuleTexts(question, allQuestions).join(` ${this.languageService.getTranslationByKey('FEEDBACKLOGICOR')} `);
	}

	private buildConditionText(
		sourceQuestion: FeedbackStructureQuestionFormQuestion,
		logicRule: FeedbackStructureQuestionLogicRule
	): string {
		const questionLabel = this.buildQuestionLabel(sourceQuestion);
		const operatorText = this.buildOperatorText(logicRule.operator);
		const valueText = this.buildConditionValueText(sourceQuestion, logicRule);

		return valueText ? `${questionLabel} ${operatorText} ${valueText}` : `${questionLabel} ${operatorText}`;
	}

	private buildActionText(
		sourceQuestion: FeedbackStructureQuestionFormQuestion,
		logicRule: FeedbackStructureQuestionLogicRule,
		allQuestions: FeedbackStructureQuestionFormQuestion[],
		affectedQuestion?: FeedbackStructureQuestionFormQuestion
	): string {
		if (logicRule.action === FeedbackLogicRuleAction.END_FEEDBACK) {
			if (affectedQuestion && !this.isSameQuestion(sourceQuestion, affectedQuestion)) {
				return `${this.buildQuestionLabel(affectedQuestion)} ${this.languageService.getTranslationByKey('FEEDBACKLOGICRULEWILLNOTSHOW')}`;
			}

			return this.languageService.getTranslationByKey('FEEDBACKLOGICRULEACTIONENDFEEDBACK');
		}

		const targetQuestions = this.resolveTargetQuestions(sourceQuestion, logicRule, allQuestions);
		if (targetQuestions.length === 0) {
			return this.languageService.getTranslationByKey('FEEDBACKLOGICRULEACTIONSHOWQUESTIONS');
		}

		if (targetQuestions.length === 1) {
			return `${this.languageService.getTranslationByKey('FEEDBACKLOGICRULEACTIONSHOWQUESTION')} ${this.buildQuestionLabel(targetQuestions[0]!)}`;
		}

		return `${this.languageService.getTranslationByKey('FEEDBACKLOGICRULEACTIONSHOWQUESTIONS')} ${this.buildQuestionLabel(targetQuestions[0]!)} ${this.languageService.getTranslationByKey('FEEDBACKLOGICRULETHROUGH')} ${this.buildQuestionLabel(targetQuestions[targetQuestions.length - 1]!)}`;
	}

	private buildConditionValueText(
		sourceQuestion: FeedbackStructureQuestionFormQuestion,
		logicRule: FeedbackStructureQuestionLogicRule
	): string | null {
		switch (sourceQuestion.type) {
			case FeedbackQuestionType.SINGLE_SELECTION:
			case FeedbackQuestionType.MULTI_SELECTION:
			case FeedbackQuestionType.DROPDOWN: {
				const option = this.resolveRuleOption(sourceQuestion, logicRule);
				return option ? this.quote(this.buildOptionLabel(option)) : null;
			}
			default:
				return this.formatValue(logicRule.value);
		}
	}

	private buildOperatorText(operator: FeedbackLogicRuleOperator): string {
		switch (operator) {
			case FeedbackLogicRuleOperator.SELECTED:
				return this.languageService.getTranslationByKey('FEEDBACKLOGICSELECTEDMENU');
			case FeedbackLogicRuleOperator.NOT_SELECTED:
				return this.languageService.getTranslationByKey('FEEDBACKLOGICNOTSELECTED');
			case FeedbackLogicRuleOperator.IS_EMPTY:
				return this.languageService.getTranslationByKey('FEEDBACKLOGICISEMPTYMENU');
			case FeedbackLogicRuleOperator.IS_NOT_EMPTY:
				return this.languageService.getTranslationByKey('FEEDBACKLOGICNOTEMPTYMENU');
			case FeedbackLogicRuleOperator.LESS_THAN:
				return this.languageService.getTranslationByKey('FEEDBACKLOGICLESSTHANMENU');
			case FeedbackLogicRuleOperator.GREATER_THAN:
				return this.languageService.getTranslationByKey('FEEDBACKLOGICGREATERTHANMENU');
			case FeedbackLogicRuleOperator.EQUAL_TO:
				return this.languageService.getTranslationByKey('FEEDBACKLOGICEQUALTOMENU');
			case FeedbackLogicRuleOperator.NOT_EQUAL_TO:
				return this.languageService.getTranslationByKey('FEEDBACKLOGICNOTEQUALTOMENU');
			case FeedbackLogicRuleOperator.CONTAINS:
				return this.languageService.getTranslationByKey('FEEDBACKLOGICCONTAINSMENU');
			case FeedbackLogicRuleOperator.NOT_CONTAINS:
				return this.languageService.getTranslationByKey('FEEDBACKLOGICNOTCONTAINSMENU');
		}
	}

	private buildQuestionLabel(question: FeedbackStructureQuestionFormQuestion): string {
		return `${this.languageService.getTranslationByKey('FEEDBACKQUESTIONCHARACTER')} ${question.position + 1}`;
	}

	private buildOptionLabel(option: FeedbackStructureQuestionOption): string {
		const label = option.label?.trim();
		return label ? label : `${this.languageService.getTranslationByKey('FEEDBACKANSWER')} ${option.position + 1}`;
	}

	private ruleAffectsQuestion(
		sourceQuestion: FeedbackStructureQuestionFormQuestion,
		logicRule: FeedbackStructureQuestionLogicRule,
		targetQuestion: FeedbackStructureQuestionFormQuestion,
		allQuestions: FeedbackStructureQuestionFormQuestion[]
	): boolean {
		if (this.isSameQuestion(sourceQuestion, targetQuestion)) {
			return false;
		}

		if (logicRule.action === FeedbackLogicRuleAction.END_FEEDBACK) {
			return targetQuestion.position > sourceQuestion.position;
		}

		return this.resolveTargetQuestions(sourceQuestion, logicRule, allQuestions)
			.some((question) => this.isSameQuestion(question, targetQuestion));
	}

	private resolveTargetQuestions(
		sourceQuestion: FeedbackStructureQuestionFormQuestion,
		logicRule: FeedbackStructureQuestionLogicRule,
		allQuestions: FeedbackStructureQuestionFormQuestion[]
	): FeedbackStructureQuestionFormQuestion[] {
		const sortedQuestions = this.sortedQuestions(allQuestions);

		if ((logicRule.target_question_client_ids?.length ?? 0) > 0) {
			return sortedQuestions.filter((question) => (
				question.client_id !== undefined
				&& logicRule.target_question_client_ids?.includes(question.client_id)
				&& !this.isSameQuestion(question, sourceQuestion)
			));
		}

		const startPosition = this.resolveQuestionPosition(logicRule.start_question_client_id, logicRule.start_question_position, sortedQuestions);
		const endPosition = this.resolveQuestionPosition(logicRule.end_question_client_id, logicRule.end_question_position, sortedQuestions);
		if (startPosition === null || endPosition === null || endPosition < startPosition) {
			return [];
		}

		return sortedQuestions.filter((question) =>
			question.position >= startPosition
			&& question.position <= endPosition
			&& !this.isSameQuestion(question, sourceQuestion)
		);
	}

	private resolveRuleOption(
		sourceQuestion: FeedbackStructureQuestionFormQuestion,
		logicRule: FeedbackStructureQuestionLogicRule
	): FeedbackStructureQuestionOption | null {
		if (logicRule.source_option_client_id) {
			return sourceQuestion.options.find((option) => option.client_id === logicRule.source_option_client_id) ?? null;
		}

		const optionPosition = sourceQuestion.type === FeedbackQuestionType.DROPDOWN
			? this.toOptionalNumber(logicRule.value)
			: logicRule.source_option_position;
		if (optionPosition === null || optionPosition === undefined) {
			return null;
		}

		return sourceQuestion.options.find((option) => option.position === optionPosition) ?? null;
	}

	private resolveQuestionPosition(
		questionClientId: string | null | undefined,
		fallbackPosition: number | null | undefined,
		allQuestions: FeedbackStructureQuestionFormQuestion[]
	): number | null {
		if (questionClientId) {
			const question = allQuestions.find((currentQuestion) => currentQuestion.client_id === questionClientId);
			if (question) {
				return question.position;
			}
		}

		return typeof fallbackPosition === 'number' ? fallbackPosition : null;
	}

	private formatValue(value: string | null | undefined): string | null {
		if (!value?.trim()) {
			return null;
		}

		return /^-?\d+(\.\d+)?$/.test(value) ? value : this.quote(value);
	}

	private quote(value: string): string {
		return `'${value}'`;
	}

	private sortedQuestions(allQuestions: FeedbackStructureQuestionFormQuestion[]): FeedbackStructureQuestionFormQuestion[] {
		return [...allQuestions].sort((left, right) => left.position - right.position);
	}

	private isSameQuestion(
		firstQuestion: FeedbackStructureQuestionFormQuestion,
		secondQuestion: FeedbackStructureQuestionFormQuestion
	): boolean {
		if (firstQuestion.client_id && secondQuestion.client_id) {
			return firstQuestion.client_id === secondQuestion.client_id;
		}

		if (firstQuestion.id !== undefined && secondQuestion.id !== undefined) {
			return firstQuestion.id === secondQuestion.id;
		}

		return firstQuestion.position === secondQuestion.position;
	}

	private toOptionalNumber(value: string | null | undefined): number | null {
		if (value === null || value === undefined || value === '') {
			return null;
		}

		const numberValue = Number(value);
		return Number.isFinite(numberValue) ? numberValue : null;
	}
}
